/**
 * Display-level brand aliases for the store view (MOT-117).
 *
 * Stores know some brands by a different name than the one on the data row:
 * Olympus cameras are sold as OM System, Lumix is Panasonic's camera line, and
 * Fujifilm instax is a Fujifilm product line. Wherever the store view lists or
 * filters brands, pass the name through `storeBrandLabel` so one brand shows up
 * once under the name the store expects.
 *
 * This is display only. It does not change stored brand names, and it does not
 * touch `brandIdentity.ts`, which deliberately keeps Olympus / OM System and
 * Lumix / Panasonic apart for matching and dealer authorization.
 */
import { brandIdentityKey } from './brandIdentity';

/** Alias key (see `brandIdentityKey`: lower-case, letters and digits only) to display name. */
export const STORE_BRAND_ALIASES: Record<string, string> = {
  olympus: 'OM System',
  lumix: 'Panasonic',
  fujifilminstax: 'Fujifilm',
};

/** The name the store view shows for a brand. Unaliased names are returned trimmed and otherwise unchanged. */
export function storeBrandLabel(name: string | null | undefined): string {
  const raw = String(name ?? '').trim();
  if (!raw) return '';
  const key = brandIdentityKey(raw);
  return Object.prototype.hasOwnProperty.call(STORE_BRAND_ALIASES, key) ? STORE_BRAND_ALIASES[key] : raw;
}

/** True when two brand names are the same brand once aliases are applied. */
export function storeBrandsMatch(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = brandIdentityKey(storeBrandLabel(a));
  return !!left && left === brandIdentityKey(storeBrandLabel(b));
}

/** Unique, sorted display names for a list of raw brand names, e.g. for a filter dropdown. */
export function storeBrandOptions(names: Array<string | null | undefined>): string[] {
  const byKey = new Map<string, string>();
  for (const name of names) {
    const label = storeBrandLabel(name);
    const key = brandIdentityKey(label);
    if (key && !byKey.has(key)) byKey.set(key, label);
  }
  return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b));
}
